import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ModuleFrame, { Chip } from '../../components/ModuleFrame';
import { useEntityList, friendlyError } from '../../data/entities';
import { POLICY_AREAS, POLICY_LABELS, POLICY_EDIT_ROUTE, restorePolicyRevision, usePolicyRevisions } from '../../data/policy';

// Org policy history — every change to guardrails, review routing,
// permissions, environment controls, cost limits and the model ladder as a
// numbered revision (who, when, what changed), with before/after and a
// one-click restore. Reads orgs/{o}/policyRevisions (server-written, admin
// read); restore goes through POST /orgs/:o/policy/revisions/:id/restore and
// is itself a new revision — the list only ever grows.
//
//   <PolicyHistory org isAdmin />                  the Administration tab
//   <PolicyHistory org isAdmin area="guardrails" compact />
//                                                  one-line footer under an editor

const when = (t) => (t ? new Date(t).toLocaleString() : '');
const short = (v) => {
  if (v == null) return '—';
  if (typeof v !== 'object') return String(v);
  const s = JSON.stringify(v);
  return s.length > 80 ? `${s.slice(0, 77)}…` : s;
};

function Diff({ rev }) {
  const keys = rev.changedKeys?.length ? rev.changedKeys : [...new Set([...Object.keys(rev.before || {}), ...Object.keys(rev.after || {})])];
  return (
    <table className="mf-table" style={{ marginTop: 8 }}>
      <thead><tr><th>Setting</th><th>Before (v{Math.max(0, (rev.version || 1) - 1)})</th><th>After (v{rev.version})</th></tr></thead>
      <tbody>
        {keys.map((k) => (
          <tr key={k}>
            <td><code>{k}</code></td>
            <td className="mf-muted" style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11.5, overflowWrap: 'anywhere' }}>{short(rev.before?.[k])}</td>
            <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11.5, overflowWrap: 'anywhere' }}>{short(rev.after?.[k])}</td>
          </tr>
        ))}
        {!keys.length && <tr><td colSpan={3} className="mf-muted">{rev.after == null ? 'cleared — back to the platform defaults' : 'no top-level key differs'}</td></tr>}
      </tbody>
    </table>
  );
}

function Row({ orgId, rev, isCurrent, isAdmin, members, byId, onMsg }) {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const who = rev.actorEmail || (members || []).find((m) => m.uid === rev.actor)?.email || rev.actor;
  const restoredFrom = rev.restoredFromId ? byId.get(rev.restoredFromId) : null;
  const restore = async () => {
    setBusy(true); onMsg('');
    try {
      await restorePolicyRevision(orgId, rev.id);
      onMsg(`${POLICY_LABELS[rev.area] || rev.area} is back to v${rev.version} — recorded as a new revision.`, 'ok');
      setConfirm(false);
    } catch (e) { onMsg(friendlyError(e), 'err'); } finally { setBusy(false); }
  };
  return (
    <div className="mf-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6 }} data-nova-note={`${POLICY_LABELS[rev.area] || rev.area} v${rev.version} by ${who} at ${when(rev.createdAt)}${isCurrent ? ' (current)' : ''}`}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Chip tone={isCurrent ? 'ok' : 'muted'}>{isCurrent ? 'current' : `v${rev.version}`}</Chip>
        <strong>{POLICY_LABELS[rev.area] || rev.area}</strong>
        {isCurrent && <span className="mf-muted">v{rev.version}</span>}
        <span className="mf-muted">{when(rev.createdAt)} · {who}</span>
        {rev.restoredFromId && <Chip tone="info" title={rev.restoredFromId}>restore of v{restoredFrom?.version ?? '?'}</Chip>}
        <span style={{ flex: 1 }} />
        <span className="mf-muted" style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11.5 }}>{(rev.changedKeys || []).slice(0, 4).join(', ')}{(rev.changedKeys || []).length > 4 ? ` +${rev.changedKeys.length - 4}` : ''}</span>
        <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setOpen((o) => !o)} aria-expanded={open}>{open ? 'Hide' : 'Before / after'}</button>
      </div>
      {rev.reason && <p className="cs-help" style={{ margin: 0 }}>{rev.reason}</p>}
      {open && <Diff rev={rev} />}
      {!isCurrent && isAdmin && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }} data-agent-guard>
          {!confirm && <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={busy} onClick={() => setConfirm(true)}>Restore this version</button>}
          {confirm && (
            <>
              <span className="mf-muted">Replace the current {POLICY_LABELS[rev.area] || rev.area} with v{rev.version}?</span>
              <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" disabled={busy} onClick={restore}>{busy ? 'Restoring…' : `Yes, restore v${rev.version}`}</button>
              <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" disabled={busy} onClick={() => setConfirm(false)}>Cancel</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function PolicyHistory({ org, isAdmin, area: fixedArea = null, compact = false }) {
  const [params, setParams] = useSearchParams();
  const area = fixedArea || params.get('area') || '';
  const { revisions, loading } = usePolicyRevisions(org.id, area || null, { enabled: !!isAdmin });
  const { docs: members } = useEntityList('member', { org: org.id }, { enabled: !compact && !!isAdmin });
  const [msg, setMsg] = useState({ text: '', tone: '' });
  const onMsg = (text, tone = '') => setMsg({ text, tone });

  // Latest revision per area = the one the org currently runs.
  const currentIds = useMemo(() => {
    const seen = new Map();
    for (const r of revisions) if (!seen.has(r.area) || (r.version || 0) > (seen.get(r.area).version || 0)) seen.set(r.area, r);
    return new Set([...seen.values()].map((r) => r.id));
  }, [revisions]);
  const byId = useMemo(() => new Map(revisions.map((r) => [r.id, r])), [revisions]);

  if (!isAdmin) {
    return compact ? null : <ModuleFrame.Panel title="Policy history"><p className="cs-help">Policy history is visible to org admins and owners.</p></ModuleFrame.Panel>;
  }
  if (compact) {
    const latest = revisions[0];
    const to = `/orgs/${org.id}/administration?tab=history${area ? `&area=${area}` : ''}`;
    return (
      <p className="mf-muted" style={{ marginTop: 10, fontSize: 12 }} data-nova-note={latest ? `${POLICY_LABELS[area] || area} is at v${latest.version}` : `${POLICY_LABELS[area] || area}: no changes recorded yet`}>
        {latest
          ? <>v{latest.version} · changed by {latest.actorEmail || latest.actor} {when(latest.createdAt)}{latest.restoredFromId ? ' (a restore)' : ''} · </>
          : <>No changes recorded yet — the defaults are in effect · </>}
        <Link to={to}>History →</Link>
      </p>
    );
  }

  const setArea = (a) => { const next = new URLSearchParams(params); if (a) next.set('area', a); else next.delete('area'); setParams(next, { replace: true }); };
  return (
    <ModuleFrame.Panel
      title="Policy history"
      meta="every change to the organization's policy, numbered — restore any earlier version"
      actions={(
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }} role="tablist" aria-label="Policy area">
          <button type="button" className={`cs-btn cs-btn-sm ${!area ? 'cs-btn-primary' : 'cs-btn-ghost'}`} onClick={() => setArea('')}>All</button>
          {POLICY_AREAS.map((a) => <button key={a} type="button" className={`cs-btn cs-btn-sm ${area === a ? 'cs-btn-primary' : 'cs-btn-ghost'}`} onClick={() => setArea(a)}>{POLICY_LABELS[a]}</button>)}
        </div>
      )}
    >
      {msg.text && <p className={msg.tone === 'err' ? 'cs-form-error' : 'mf-muted'} role={msg.tone === 'err' ? 'alert' : 'status'}>{msg.text}</p>}
      {area && (
        <p className="cs-help" style={{ marginTop: 0 }}>
          {POLICY_LABELS[area]} is edited in <Link to={`/orgs/${org.id}/${POLICY_EDIT_ROUTE[area]}`}>its own panel</Link>; this is its record. The row marked <strong>current</strong> is what the organization runs now.
        </p>
      )}
      {loading && <p className="cs-help">Loading…</p>}
      {!loading && !revisions.length && (
        <p className="cs-help">No changes recorded yet. Guardrail toggles, routing rules, permissions, environment controls, cost limits and the model ladder all land here with who changed them and when; any earlier version can be restored.</p>
      )}
      <div className="mf-list" style={{ display: 'grid', gap: 8 }}>
        {revisions.slice(0, 200).map((r) => <Row key={r.id} orgId={org.id} rev={r} isCurrent={currentIds.has(r.id)} isAdmin={isAdmin} members={members} byId={byId} onMsg={onMsg} />)}
      </div>
    </ModuleFrame.Panel>
  );
}
